<template>
  <div class="card">
    <!-- Header -->
    <div class="card-content blue-grey darken-4 white-text">
      <div class="row" style="margin-bottom: 0">
        <div class="col s12 m7">
          <span class="card-title">Incident Reports</span>
          <div class="white-text" style="opacity: 0.85; font-size: 13px">
            Venue: <strong>{{ venueNameDisplay }}</strong>
          </div>
        </div>

        <div class="col s12 m5 right-align header-action-col">
          <button
            class="btn red darken-2 waves-effect waves-light"
            type="button"
            @click="openNewIncidentModal"
          >
            <i class="material-icons left">add</i>
            New Incident
          </button>
        </div>
      </div>
    </div>

    <!-- Collapsible -->
    <div class="card-content">
      <div v-if="loading">Loading incidents…</div>

      <div v-else-if="incidents.length === 0">No incidents found.</div>

      <ul v-else ref="collapsibleEl" class="collapsible">
        <li v-for="inc in incidents" :key="inc.id">
          <div class="collapsible-header" @click="markAsRead(inc)">
            <div
              style="
                display: flex;
                justify-content: space-between;
                width: 100%;
                align-items: center;
              "
            >
              <div>
                <strong>#{{ formatIncidentNo(inc.incidentNumber) }}</strong>
                — {{ inc.summary || inc.category || "Incident" }}
              </div>

              <span
                v-if="isUnread(inc)"
                class="new badge red"
                data-badge-caption="NEW"
              ></span>
            </div>
          </div>

          <div class="collapsible-body">
            <div><strong>Date:</strong> {{ formatDate(inc.createdAt) }}</div>
            <div>
              <strong>Incident time:</strong>
              {{ formatDate(inc.incidentAt || inc.occurredAt) }}
            </div>
            <div>
              <strong>Report started:</strong> {{ formatDate(inc.reportStartedAt) }}
            </div>
            <div><strong>Category:</strong> {{ inc.category || "—" }}</div>
            <div><strong>Completed By:</strong> {{ inc.reporterName || "—" }}</div>
            <div><strong>Job Role:</strong> {{ inc.reporterRole || "—" }}</div>
            <div>
              <strong>Captured on CCTV:</strong> {{ inc.capturedOnCctv ? "Yes" : "No" }}
            </div>

            <div style="margin-top: 8px">
              {{ inc.details || "No additional details." }}
            </div>

            <div
              v-if="Array.isArray(inc.photos) && inc.photos.length"
              style="margin-top: 12px"
            >
              <div style="font-weight: 600; margin-bottom: 8px">Photos</div>

              <div class="photo-preview-grid">
                <img
                  v-for="(p, idx) in inc.photos"
                  :key="p + idx"
                  :src="p"
                  class="materialboxed"
                  alt="Incident photo"
                />
              </div>
            </div>

            <div
              v-if="Array.isArray(inc.media) && inc.media.some((m) => m.kind === 'video')"
              style="margin-top: 12px"
            >
              <div style="font-weight: 600; margin-bottom: 8px">Videos</div>

              <div class="video-preview-grid">
                <video
                  v-for="(m, idx) in inc.media.filter((m) => m.kind === 'video')"
                  :key="(m.url || m.name || 'video') + idx"
                  :src="m.url"
                  controls
                  preload="metadata"
                ></video>
              </div>
            </div>
          </div>
        </li>
      </ul>
    </div>

    <!-- New Incident Modal -->
    <div ref="modalEl" class="modal incident-modal">
      <div class="modal-content">
        <h5 style="margin-top: 0; margin-bottom: 24px">New Incident Report</h5>

        <IncidentReportForm
          ref="incidentFormRef"
          :venue-id="venueId"
          @created="handleCreated"
          @error="handleError"
          @busy="handleBusy"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, nextTick, onBeforeUnmount } from "vue";
import { db, auth } from "@/firebase";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";
import IncidentReportForm from "@/components/incidents/IncidentReportForm.vue";

export default {
  name: "VenueIncidentPanel",

  components: {
    IncidentReportForm,
  },

  props: {
    venueId: { type: String, required: true },
  },

  setup(props) {
    const incidents = ref([]);
    const loading = ref(true);
    const venueNameDisplay = ref(props.venueId);
    const unsub = ref(null);
    const collapsibleEl = ref(null);
    const modalEl = ref(null);
    const incidentFormRef = ref(null);
    const formBusy = ref(false);

    let collapsibleInstance = null;
    let modalInstance = null;

    function getCurrentUid() {
      return auth.currentUser?.uid || null;
    }

    function formatIncidentNo(n) {
      if (!n) return "—";
      return String(n).padStart(6, "0");
    }

    function formatDate(v) {
      if (!v) return "—";

      if (typeof v?.toDate === "function") {
        return v.toDate().toLocaleString();
      }

      const dt = new Date(v);
      return Number.isNaN(dt.getTime()) ? "—" : dt.toLocaleString();
    }

    function isUnread(inc) {
      const uid = getCurrentUid();
      if (!uid) return false;

      const readBy = Array.isArray(inc.readBy) ? inc.readBy : [];
      return !readBy.includes(uid);
    }

    async function markAsRead(inc) {
      const uid = getCurrentUid();
      if (!uid) return;
      if (!isUnread(inc)) return;

      try {
        const incidentRef = doc(db, "venues", props.venueId, "incident_reports", inc.id);

        await updateDoc(incidentRef, {
          readBy: arrayUnion(uid),
        });
      } catch (e) {
        console.error("Failed to mark incident as read:", e);
      }
    }

    async function loadVenueName() {
      try {
        const venueRef = doc(db, "venues", props.venueId);
        const snap = await getDoc(venueRef);

        if (!snap.exists()) {
          venueNameDisplay.value = props.venueId;
          return;
        }

        const data = snap.data() || {};
        venueNameDisplay.value =
          data.name || data.venue_name || data.displayName || props.venueId;
      } catch {
        venueNameDisplay.value = props.venueId;
      }
    }

    function initCollapsible() {
      if (!window.M || !collapsibleEl.value) return;

      if (collapsibleInstance) {
        collapsibleInstance.destroy();
      }

      collapsibleInstance = window.M.Collapsible.init(collapsibleEl.value, {
        accordion: false,
      });

      const boxed = collapsibleEl.value.querySelectorAll(".materialboxed");
      if (boxed.length) {
        window.M.Materialbox.init(boxed);
      }
    }

    function initModal() {
      if (!window.M || !modalEl.value) return;

      if (modalInstance) {
        modalInstance.destroy();
      }

      modalInstance = window.M.Modal.init(modalEl.value, {
        dismissible: !formBusy.value,
        opacity: 0.5,
      });
    }

    function openNewIncidentModal() {
      if (!modalInstance) {
        initModal();
      }

      modalInstance?.open();

      nextTick(() => {
        if (window.M) {
          window.M.updateTextFields();
        }

        incidentFormRef.value?.stampReportStartedNow?.();
      });
    }

    function closeNewIncidentModal() {
      modalInstance?.close();
    }

    function bindList() {
      loading.value = true;

      const venueRef = doc(db, "venues", props.venueId);
      const colRef = collection(venueRef, "incident_reports");
      const q = query(colRef, orderBy("incidentNumber", "desc"));

      if (unsub.value) {
        unsub.value();
      }

      unsub.value = onSnapshot(q, async (snap) => {
        incidents.value = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        loading.value = false;

        await nextTick();
        initCollapsible();
      });
    }

    function handleCreated(payload) {
      formBusy.value = false;
      closeNewIncidentModal();

      if (window.M?.toast) {
        window.M.toast({
          html: `Created incident #${formatIncidentNo(payload?.incidentNumber)}`,
        });
      }
    }

    function handleError(msg) {
      formBusy.value = false;

      if (window.M?.toast && msg) {
        window.M.toast({ html: msg });
      }
    }

    function handleBusy(v) {
      formBusy.value = !!v;
    }

    onMounted(async () => {
      await loadVenueName();
      bindList();

      await nextTick();
      initModal();
    });

    watch(
      () => props.venueId,
      async () => {
        incidents.value = [];
        await loadVenueName();
        bindList();
      }
    );

    onBeforeUnmount(() => {
      if (unsub.value) unsub.value();
      if (collapsibleInstance) collapsibleInstance.destroy();
      if (modalInstance) modalInstance.destroy();
    });

    return {
      incidents,
      loading,
      venueNameDisplay,
      collapsibleEl,
      modalEl,
      incidentFormRef,
      formBusy,
      formatIncidentNo,
      formatDate,
      isUnread,
      markAsRead,
      openNewIncidentModal,
      handleCreated,
      handleError,
      handleBusy,
    };
  },
};
</script>

<style scoped>
.header-action-col {
  margin-top: 8px;
}

.incident-modal {
  width: 90%;
  max-width: 900px;
  max-height: 85vh;
  height: auto;
  overflow-y: auto;
}

.photo-preview-grid,
.video-preview-grid {
  margin-top: 10px;
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

@media (min-width: 993px) {
  .photo-preview-grid,
  .video-preview-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.photo-preview-grid img,
.video-preview-grid video {
  width: 100%;
  height: 160px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

/* Mobile optimisation only */
@media (max-width: 600px) {
  .card > .card-content:first-child {
    padding: 16px;
  }

  .card-title {
    font-size: 1.45rem;
    line-height: 1.25;
  }

  .header-action-col {
    text-align: left !important;
    margin-top: 12px;
  }

  .header-action-col .btn {
    width: 100%;
  }

  .collapsible-header > div {
    display: block !important;
  }

  .collapsible-header .badge {
    margin-top: 8px;
  }

  .collapsible-body {
    padding: 14px;
    overflow-wrap: anywhere;
  }

  .photo-preview-grid,
  .video-preview-grid {
    grid-template-columns: 1fr;
  }

  .photo-preview-grid img,
  .video-preview-grid video {
    height: 190px;
  }

  .incident-modal {
    width: 94% !important;
    max-height: 92vh;
    top: 4% !important;
  }

  .incident-modal .modal-content {
    padding: 18px;
  }
}
</style>
